import { ComponentProps, ReactNode } from 'react';
import { ThemeProvider as LegacyThemeProvider } from '@croquiscom/zds';
import { ThemeProvider as ZDSThemeProvider } from '@croquiscom-pvt/zds';

const LEGACY_THEME_LOOKUP = new Set(['light', 'dark']);

interface Props {
  /**
   * ThemeProvider 설정
   *
   * - `"light"`, `"dark"` 등 고정 테마: LegacyThemeProvider + ThemeProvider로 감쌉니다.
   * - `"system"`: ThemeProvider를 렌더링하지 않습니다. 페이지별로 직접 ThemeProvider를 적용하세요.
   *
   * LegacyThemeProvider는 항상 렌더링됩니다.
   */
  theme?: ComponentProps<typeof ZDSThemeProvider>['colorScheme'];
  children?: ReactNode;
}

/**
 * ZDS 테마 Provider를 관리하는 컴포넌트입니다.
 *
 * `theme="system"`인 경우 `ThemeProvider`(`@croquiscom-pvt/zds`)를 렌더링하지 않습니다.
 * `colorScheme="system"`은 페이지별로 ThemeProvider를 직접 적용해야 하는 정책이기 때문입니다.
 * (루트에서 system으로 선언 시, 하위 페이지에서 중첩 선언 에러 발생)
 *
 * `LegacyThemeProvider`(`@croquiscom/zds`)는 항상 렌더링됩니다.
 */
export const ThemeProvider = ({ theme = 'light', children }: Props) => {
  const legacyTheme = LEGACY_THEME_LOOKUP.has(theme)
    ? (theme as ComponentProps<typeof LegacyThemeProvider>['name'])
    : 'light';
  if (theme === 'system') {
    return <LegacyThemeProvider name={legacyTheme}>{children}</LegacyThemeProvider>;
  }

  return (
    <LegacyThemeProvider name={legacyTheme}>
      <ZDSThemeProvider colorScheme={theme}>{children}</ZDSThemeProvider>
    </LegacyThemeProvider>
  );
};
